import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useParams } from 'react-router';
import {
  DeleteUnitPhotoDocument,
  GetUnitDocument,
  UpdateUnitDocument,
  SetUnitCoverPhotoDocument,
  UpdateUnitPhotoCaptionDocument,
  AddUnitCommentDocument,
  UpdateUnitCommentDocument,
  RemoveUnitCommentDocument,
  AddUnitPhotoDocument,
  ConfirmUnitPhotoUploadDocument,
  AddUnitCommentMutation,
  AddUnitCommentMutationVariables,
  AddUnitPhotoMutation,
  AddUnitPhotoMutationVariables,
  ConfirmUnitPhotoUploadMutation,
  ConfirmUnitPhotoUploadMutationVariables,
  DeleteUnitPhotoMutation,
  DeleteUnitPhotoMutationVariables,
  GetUnitQuery,
  GetUnitQueryVariables,
  RemoveUnitCommentMutation,
  RemoveUnitCommentMutationVariables,
  SetUnitCoverPhotoMutation,
  SetUnitCoverPhotoMutationVariables,
  UpdateUnitCommentMutation,
  UpdateUnitCommentMutationVariables,
  UpdateUnitMutation,
  UpdateUnitMutationVariables,
  UpdateUnitPhotoCaptionMutation,
  UpdateUnitPhotoCaptionMutationVariables,
  UndeleteUnitMutation,
  UndeleteUnitMutationVariables,
  UndeleteUnitDocument,
} from '../../generated/graphql';
import { Stack, Typography, Grid, Tabs, Tab, Box, useTheme, useMediaQuery, Chip, Divider } from '@mui/material';

import { UnitPictureView } from './components/unit-picture-view';
import { BasePage } from '../../components/base-page';
import { useSearchParams } from 'react-router';
import { UnitPhotoSection } from './components/unit-photo-section';

import { UnitBasicInfoSection } from './components/unit-basic-info-section';
import { UnitDocumentsSection } from './components/unit-documents-section';
import { UnitTasksSection } from './components/unit-tasks-section';
import { UnitRecurringTasksSection } from './components/unit-recurring-tasks-section';
import { UnitExpensesSection } from './components/unit-expenses-section';
import { UnitRecurringExpensesSection } from './components/unit-recurring-expenses-section';

import { useAlert } from '@dwelvio/react-alert';

import { UnitCoverPhoto } from './components/cover-photo';
import { UnitDetailActionMenu } from './components/unit-detail-menu';
import { useApp } from '../../components/app-context';
import { InputComment } from '../../components/input-comment';
import { CommentList } from '../../components/comments-list';
import { uploadFile } from '../../lib/upload-files';

import { DeletedItemBanner } from '../../components/deleted-item-banner';

import { sanitizeFilename, fileContentDisposition } from '@dwelvio/shared';

interface FormFields {
  name: string;
  address: string;
  description: string;
  note: string;
}

const initialState: FormFields = {
  name: '',
  address: '',
  description: '',
  note: '',
};

const TaskTabArray = ['tasks', 'recurring'] as const;
type TaskTabType = (typeof TaskTabArray)[number];

const ExpenseTabArray = ['expenses', 'recurring-expenses'] as const;
type ExpenseTabType = (typeof ExpenseTabArray)[number];

export function MockUnitDetailPage() {
  const { unitId } = useParams();
  const [fetchUnit, { data, loading: initialLoading, error, refetch: refetchUnit }] = useLazyQuery<
    GetUnitQuery,
    GetUnitQueryVariables
  >(GetUnitDocument);
  useEffect(() => {
    //console.log('Fetching unit with unitId=', unitId);
    if (unitId) {
      fetchUnit({
        variables: { id: unitId },
      });
    }
  }, [unitId, fetchUnit]);
  return (
    <div>
      Mock Unit Detail Page {unitId} {data?.getUnit?.name}
    </div>
  );
}

export function UnitDetailPage() {
  const { show } = useAlert();
  const { unitId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const app = useApp();
  const theme = useTheme();

  const [selectedPhoto, setSelectedPhoto] = useState<number | null>(null);

  const [fetchUnit, { data, loading: initialLoading, error, refetch: refetchUnit }] = useLazyQuery<
    GetUnitQuery,
    GetUnitQueryVariables
  >(GetUnitDocument);

  const [undeleteUnit, { loading: undeleteLoading }] = useMutation<UndeleteUnitMutation, UndeleteUnitMutationVariables>(
    UndeleteUnitDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa obnoviť nehnuteľnosť.', e.message),
    },
  );

  const [setCoverPhoto] = useMutation<SetUnitCoverPhotoMutation, SetUnitCoverPhotoMutationVariables>(
    SetUnitCoverPhotoDocument,
  );

  const [deletePhoto] = useMutation<DeleteUnitPhotoMutation, DeleteUnitPhotoMutationVariables>(DeleteUnitPhotoDocument);
  const [updatePhotoCaption] = useMutation<UpdateUnitPhotoCaptionMutation, UpdateUnitPhotoCaptionMutationVariables>(
    UpdateUnitPhotoCaptionDocument,
  );

  const [updateUnit, { loading: updateLoading }] = useMutation<UpdateUnitMutation, UpdateUnitMutationVariables>(
    UpdateUnitDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa aktualizovať nehnuteľnosť.', e.message);
      },
    },
  );

  const handleUnitEdit = useCallback(
    ({ name, address, description, note }: FormFields) => {
      if (!unitId) return;
      updateUnit({ variables: { input: { id: unitId, name, address, description, note } } });
    },
    [unitId, updateUnit],
  );

  const [addComment] = useMutation<AddUnitCommentMutation, AddUnitCommentMutationVariables>(AddUnitCommentDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa pridať komentár.', e.message);
    },
  });
  const [removeComment] = useMutation<RemoveUnitCommentMutation, RemoveUnitCommentMutationVariables>(
    RemoveUnitCommentDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa zmazať komentár.', e.message);
      },
    },
  );
  const [updateComment] = useMutation<UpdateUnitCommentMutation, UpdateUnitCommentMutationVariables>(
    UpdateUnitCommentDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa aktualizovať komentár.', e.message);
      },
    },
  );

  const [uploadInProgress, setUploadInProgress] = useState(false);

  const [addUnitPhoto] = useMutation<AddUnitPhotoMutation, AddUnitPhotoMutationVariables>(AddUnitPhotoDocument);
  const [confirmUpload] = useMutation<ConfirmUnitPhotoUploadMutation, ConfirmUnitPhotoUploadMutationVariables>(
    ConfirmUnitPhotoUploadDocument,
  );

  const handlePhotoUpload = useCallback(
    async (id: string, file: File) => {
      if (!unitId) {
        return;
      }
      const headers = {
        'Content-Disposition': fileContentDisposition(file.name),
      };

      setUploadInProgress(true);
      try {
        await uploadFile(
          file,
          (file) =>
            addUnitPhoto({
              variables: { unitId, input: { filename: file.name, mimeType: file.type, size: file.size } },
            }),
          (addData) => addData.addUnitPhoto.uploadUrl,
          (file, addData) =>
            confirmUpload({
              variables: { unitId, photoId: addData.addUnitPhoto.fileId, size: file.size },
            }),
          headers,
        );
      } catch (error) {
        show.error('Chyba pri nahrávaní fotky', (error as Error).message);
      } finally {
        setUploadInProgress(false);
      }
    },
    [addUnitPhoto, confirmUpload, show, setUploadInProgress, unitId],
  );

  const tabValue: TaskTabType = (searchParams.get('tt') as TaskTabType) || 'tasks';
  if (tabValue && TaskTabArray.includes(tabValue as TaskTabType) === false) {
    searchParams.set('tt', 'tasks');
    setSearchParams(searchParams);
  }

  const expenseTabValue: ExpenseTabType = (searchParams.get('et') as ExpenseTabType) || 'expenses';
  if (expenseTabValue && ExpenseTabArray.includes(expenseTabValue as ExpenseTabType) === false) {
    searchParams.set('et', 'expenses');
    setSearchParams(searchParams);
  }

  useEffect(() => {
    if (unitId) {
      fetchUnit({
        variables: { id: unitId },
      });
    }
  }, [unitId, fetchUnit]);

  const unit = data?.getUnit;
  const unitLocked = !!unit?.deletedAt;

  return (
    <BasePage
      title={'Nehnuteľnosť - ' + unit?.name}
      loading={initialLoading || !unit}
      errorMsg={error ? 'Nepodarilo sa načítať detaily nehnuteľnosti.' : undefined}
    >
      {unit && unitId && (
        <>
          {unit.deletedAt && (
            <DeletedItemBanner
              text={`Táto nehnuteľnosť bola odstránená dňa ${app.formatDate(unit.deletedAt)}`}
              undeleteLabel="Obnoviť"
              onUndelete={() => undeleteUnit({ variables: { unitId } })}
            />
          )}

          {!unit.deletedAt && unit.status === 'archived' && (
            <Chip label={`Nechnuteľnosť archivovaná`} color="default" sx={{ marginBottom: 2 }} />
          )}
          {!unit.deletedAt && unit.status === 'renting' && (
            <Chip label={`Nehnuteľnosť je prenajímaná`} color="success" sx={{ marginBottom: 2 }} />
          )}
          {!unit.deletedAt && unit.status === 'not_renting' && (
            <Chip label={`Nehnuteľnosť nie je prenajímaná`} color="warning" sx={{ marginBottom: 2 }} />
          )}

          <Stack spacing={2} sx={{ marginTop: '1rem', marginBottom: '5rem' }}>
            <Grid container columnSpacing={2} rowGap={2}>
              <Grid container size={{ xs: 12, md: 7 }}>
                <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                  <UnitCoverPhoto unit={unit} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                  <UnitBasicInfoSection
                    unit={unit}
                    onEdit={handleUnitEdit}
                    updateLoading={updateLoading}
                    disabled={unitLocked}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                  <Divider sx={{ marginY: 3 }} />
                  <UnitPhotoSection
                    unit={unit}
                    onSelectPhoto={setSelectedPhoto}
                    handlePhotoUpload={handlePhotoUpload}
                    uploadInProgress={uploadInProgress}
                    disabled={unitLocked}
                    onError={(message) => show.error('Chyba', message)}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                  <Divider sx={{ marginY: 3 }} />
                  <UnitDocumentsSection
                    unitId={unit.id}
                    files={unit.unitFiles}
                    documents={unit.documents}
                    disabled={unitLocked}
                  />
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <UnitDetailActionMenu unit={unit} disabled={unitLocked} />
                <Typography variant="h2">Komentáre</Typography>
                <InputComment
                  label="Pridať komentár"
                  disabled={unitLocked}
                  onSubmit={(comment) => addComment({ variables: { unitId, input: { comment } } })}
                  multiline
                  clearOnSubmit
                />
                <Box maxHeight={{ xs: '20rem', sm: '20rem' }} overflow={{ overflowY: 'scroll' }} sx={{ marginTop: 2 }}>
                  <CommentList
                    comments={unit.comments}
                    onDelete={(c) => removeComment({ variables: { unitId, commentId: c.id } })}
                    onEdit={(commentId, comment) =>
                      updateComment({ variables: { unitId, input: { id: commentId, comment } } })
                    }
                  />
                </Box>
              </Grid>
            </Grid>

            <Tabs
              value={tabValue}
              variant="scrollable"
              scrollButtons="auto"
              onChange={(e, v) => {
                searchParams.set('tt', v);
                setSearchParams(searchParams);
              }}
              sx={{ marginBottom: '1rem' }}
            >
              <Tab label="Úlohy" value="tasks" />
              <Tab label="Pravidelné úlohy" value="recurring" />
            </Tabs>

            {tabValue === 'tasks' && <UnitTasksSection unitId={unit.id} tasks={unit.tasks} disabled={unitLocked} />}

            {tabValue === 'recurring' && <UnitRecurringTasksSection unitId={unit.id} disabled={unitLocked} />}

            <Divider sx={{ my: 3 }} />

            <Tabs
              value={expenseTabValue}
              variant="scrollable"
              scrollButtons="auto"
              onChange={(e, v) => {
                searchParams.set('et', v);
                setSearchParams(searchParams);
              }}
              sx={{ marginBottom: '1rem' }}
            >
              <Tab label="Výdavky" value="expenses" />
              <Tab label="Pravidelné výdavky" value="recurring-expenses" />
            </Tabs>

            {expenseTabValue === 'expenses' && (
              <UnitExpensesSection
                unitId={unit.id}
                expenses={unit.expenses}
                disabled={unitLocked}
                onRefresh={() => refetchUnit?.()}
              />
            )}

            {expenseTabValue === 'recurring-expenses' && (
              <UnitRecurringExpensesSection unitId={unit.id} disabled={unitLocked} />
            )}
          </Stack>

          {selectedPhoto !== null && unit && (
            <UnitPictureView
              unit={unit}
              selectedPhoto={selectedPhoto}
              onClose={() => setSelectedPhoto(null)}
              onSetAsCoverPhoto={(photoId) => {
                setCoverPhoto({ variables: { unitId: unit.id, photoId } });
                setSelectedPhoto(null);
              }}
              onDeletePhoto={(photoId) => {
                deletePhoto({ variables: { unitId: unit.id, photoId } });
                setSelectedPhoto(null);
              }}
              onCaptionChange={(photoId, caption) => {
                updatePhotoCaption({ variables: { unitId: unit.id, photoId, caption } });
              }}
            />
          )}
        </>
      )}
    </BasePage>
  );
}
